import { useCallback, useEffect, useReducer, useRef } from 'react'
import type {
  Contract,
  Depth,
  Fill,
  Health,
  OwnStats,
  ServerMessage,
  ShadowOrder,
  Ticker,
  Trade,
} from './types'

/**
 * İşlem sayısı üst sınırı. Görüntüleme sınırı DEĞİL — günün tamamı gösteriliyor;
 * bu yalnızca kontrolsüz büyümeye karşı emniyet freni ve API'nin `count` üst
 * sınırıyla aynı. Bir kontratın günlük eşleşme sayısı bunun çok altında kalır.
 */
const TRADE_LIMIT = 5000

/** Kopan bağlantı için geri çekilme: 1s, 2s, 4s… en fazla 10s. */
const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 10_000

export interface MarketState {
  connected: boolean
  health: Health | null
  contracts: Contract[]
  board: Record<string, Ticker>
  depth: Depth | null
  trades: Trade[]
  /** Seçili kontratın gölge dolumları, yeniden eskiye. */
  fills: Fill[]
  /** Seçili kontratın gölge emirleri. */
  orders: ShadowOrder[]
  /** TÜM kontratlar için kendi teklif/pozisyon özetimiz (`B.F`, `NetP`). */
  own: Record<string, OwnStats>
  selected: string | null
}

const initialState: MarketState = {
  connected: false,
  health: null,
  contracts: [],
  board: {},
  depth: null,
  trades: [],
  fills: [],
  orders: [],
  own: {},
  selected: null,
}

type Action =
  | { kind: 'connected' }
  | { kind: 'disconnected' }
  | { kind: 'message'; message: ServerMessage }
  | { kind: 'selecting'; contract: string }
  | { kind: 'history'; contract: string; trades: Trade[] }

/** Yeni gelenler önde, `id` ile tekilleştirilmiş, sınırlı. */
function mergeTrades(incoming: Trade[], existing: Trade[]): Trade[] {
  const seen = new Set<string>()
  const merged: Trade[] = []
  for (const trade of [...incoming, ...existing]) {
    if (seen.has(trade.id)) continue
    seen.add(trade.id)
    merged.push(trade)
    if (merged.length >= TRADE_LIMIT) break
  }
  return merged
}

/**
 * Dolumlar için tekilleştirme anahtarı: bir emir bir gerçek işlemden en fazla
 * bir kez dolar, yani (emir, işlem) çifti benzersizdir.
 */
function fillKey(fill: Fill): string {
  return `${fill.orderId}-${fill.tradeId}`
}

function mergeFills(incoming: Fill[], existing: Fill[]): Fill[] {
  const seen = new Set<string>()
  const merged: Fill[] = []
  for (const fill of [...incoming, ...existing]) {
    const key = fillKey(fill)
    if (seen.has(key)) continue
    seen.add(key)
    merged.push(fill)
  }
  return merged
}

function reducer(state: MarketState, action: Action): MarketState {
  switch (action.kind) {
    case 'connected':
      return { ...state, connected: true }

    case 'disconnected':
      return { ...state, connected: false }

    // Sunucu yeni kontratın anlık görüntüsünü göndermeden önce seçimi
    // işaretle: tıklama anında vurgu değişsin, ekran donuk durmasın.
    case 'selecting':
      // Zaten seçili kontrata tekrar tıklamak hiçbir şeyi değiştirmemeli.
      // Aksi hâlde derinlik ve eşleşmeler temizleniyor; derinlik saniyelik
      // güncellemeyle geri geliyor ama eşleşmeler yeni bir işlem olana kadar
      // boş kalıyordu.
      if (action.contract === state.selected) return state
      // `own` temizlenmiyor: tüm kontratları kapsıyor, seçimle ilgisi yok.
      return { ...state, selected: action.contract, depth: null, trades: [], fills: [], orders: [] }

    // Günün geçmişi REST'ten gelir (WS yalnız yeni işlemleri taşır). Arada
    // WS'ten işlem düşmüş olabilir; birleştirirken `id` ile tekilleştiriyoruz.
    case 'history':
      if (action.contract !== state.selected) return state
      return { ...state, trades: mergeTrades(state.trades, action.trades) }

    case 'message': {
      const msg = action.message
      if (msg.type === 'snapshot') {
        // Anlık görüntü YALNIZ hub'ın tamponunu taşır (bağlantı açıldıktan
        // sonraki işlemler); günün geçmişi REST'ten ayrıca yüklenmişti. Aynı
        // kontratın anlık görüntüsü gelirse listeyi değiştirmek yerine
        // birleştiriyoruz — aksi hâlde geçmiş siliniyor ve `selected`
        // değişmediği için REST tekrar çağrılmıyor, liste bir daha dolmuyor.
        // (Kontrat gerçekten değiştiyse değiştirmek doğru: geçmişi yeni
        // kontrat için baştan çekeceğiz.)
        const ayniKontrat = msg.selected != null && msg.selected === state.selected
        return {
          connected: true,
          health: msg.health,
          contracts: msg.contracts,
          board: msg.board,
          depth: msg.depth,
          trades: ayniKontrat
            ? mergeTrades(msg.trades, state.trades)
            : msg.trades.slice(0, TRADE_LIMIT),
          // Dolumlar ve emirler tamamıyla gelir (motor hepsini bellekte tutuyor),
          // işlemlerdeki gibi geçmişi REST'ten tamamlama derdi yok.
          fills: msg.fills,
          orders: msg.orders,
          own: msg.own,
          selected: msg.selected,
        }
      }

      const board = { ...state.board, ...msg.board }
      for (const key of msg.removedContracts) delete board[key]

      return {
        ...state,
        health: msg.health ?? state.health,
        contracts: msg.contracts ?? state.contracts,
        board,
        depth: msg.depth ?? state.depth,
        trades: msg.trades.length ? mergeTrades(msg.trades, state.trades) : state.trades,
        fills: msg.fills.length ? mergeFills(msg.fills, state.fills) : state.fills,
        orders: msg.orders ?? state.orders,
        own: msg.own ?? state.own,
      }
    }
  }
}

function socketUrl(contract: string | null): string {
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
  const query = contract ? `?contract=${encodeURIComponent(contract)}` : ''
  return `${scheme}://${window.location.host}/ws${query}`
}

export function useMarketSocket() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const socketRef = useRef<WebSocket | null>(null)
  const attemptRef = useRef(0)

  // Güncel seçim ref'te de tutuluyor: bağlantı efekti bir kez kuruluyor, seçimi
  // state'ten okusa her kontrat değişiminde soket kopup yeniden kurulurdu.
  // Yeniden bağlanma anında sunucuya taşınacak değer buradan okunur.
  const selectedRef = useRef<string | null>(null)
  useEffect(() => {
    selectedRef.current = state.selected
  }, [state.selected])

  useEffect(() => {
    let closed = false
    let timer: number | undefined

    const connect = () => {
      if (closed) return
      const socket = new WebSocket(socketUrl(selectedRef.current))
      socketRef.current = socket

      // Her olay işleyicisi "hâlâ güncel soket miyim" diye bakıyor. Sebebi:
      // StrictMode (ve her yeniden bağlanma) sırasında eski soketin kapanışı
      // yerine geçen YENİ soketten sonra tetikleniyor; kontrolsüz bırakılırsa
      // eski soketin onclose'u socketRef'i sıfırlayıp yeni soketi görünmez
      // yapıyor — kontrat seçimi sessizce çalışmaz hâle geliyordu.
      const guncel = () => socketRef.current === socket

      socket.onopen = () => {
        if (!guncel()) return
        attemptRef.current = 0
        dispatch({ kind: 'connected' })
      }

      socket.onmessage = (event) => {
        if (!guncel()) return
        try {
          dispatch({ kind: 'message', message: JSON.parse(event.data) as ServerMessage })
        } catch (err) {
          console.error('Bozuk mesaj', err)
        }
      }

      socket.onclose = (event) => {
        if (!guncel()) return
        socketRef.current = null
        dispatch({ kind: 'disconnected' })
        if (closed) return
        // 4401: sunucu PIN istiyor (çerez süresi dolmuş ya da secret değişmiş).
        // Yeniden bağlanma döngüsüne girmek anlamsız — sayfa yenilenince
        // PinGate devreye girer. (App yalnız auth sonrası mount edildiği için
        // bu koda normalde düşülmez.)
        if (event.code === 4401) {
          window.location.reload()
          return
        }
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** attemptRef.current, RECONNECT_MAX_MS)
        attemptRef.current += 1
        timer = window.setTimeout(connect, delay)
      }

      // onerror'un ardından onclose da tetiklenir; yeniden bağlanma orada.
      socket.onerror = () => socket.close()
    }

    /**
     * Uygulama öne döndüğünde (ya da ağ geri geldiğinde) bağlantıyı tazele.
     *
     * Telefonda ekran kilitlenince soket kopuyor ve geri sayım kaldığı yerden
     * devam ediyordu: kullanıcı uygulamaya döndüğünde 10 saniyeye kadar donuk
     * tahtaya bakabiliyordu. Burada sayaç sıfırlanıyor, yani bir sonraki
     * deneme en geç 1 saniye sonra.
     *
     * Soket AYAKTAYSA dokunulmuyor: kısa bir sekme değişiminde sağlam bir
     * bağlantıyı koparmak boşuna. Ölü ama "açık" görünen soket (uykuda TCP
     * sessizce düşmüş olabilir) `onclose` gelince yakalanıyor ve sıfırlanmış
     * sayaç sayesinde anında yeniden bağlanıyor.
     */
    const uyandi = () => {
      if (closed || document.hidden) return
      attemptRef.current = 0
      const soket = socketRef.current
      if (soket && (soket.readyState === WebSocket.OPEN || soket.readyState === WebSocket.CONNECTING)) {
        return
      }
      if (timer) window.clearTimeout(timer)
      connect()
    }

    document.addEventListener('visibilitychange', uyandi)
    window.addEventListener('online', uyandi)

    connect()
    return () => {
      closed = true
      document.removeEventListener('visibilitychange', uyandi)
      window.removeEventListener('online', uyandi)
      if (timer) window.clearTimeout(timer)
      socketRef.current?.close()
    }
  }, [])

  // Günün eşleşme geçmişi: WS yalnız bağlantıdan sonrasını taşıdığı için,
  // seçilen kontratın geçmişi REST'ten ayrıca çekilir. Yoksa gün ortasında
  // açılan ekranda bant boş görünür — o kontratta yüzlerce işlem olmuşken.
  const selected = state.selected
  useEffect(() => {
    if (!selected) return
    const aborter = new AbortController()

    fetch(`/api/trades/${encodeURIComponent(selected)}?history=true&count=${TRADE_LIMIT}`, {
      signal: aborter.signal,
    })
      .then((response) => (response.ok ? response.json() : []))
      .then((trades: Trade[]) => dispatch({ kind: 'history', contract: selected, trades }))
      .catch((err) => {
        if (err.name !== 'AbortError') console.error('İşlem geçmişi alınamadı', err)
      })

    return () => aborter.abort()
  }, [selected])

  const select = useCallback((contract: string) => {
    const socket = socketRef.current
    if (!socket || socket.readyState !== WebSocket.OPEN) return
    dispatch({ kind: 'selecting', contract })
    socket.send(JSON.stringify({ action: 'select', contract }))
  }, [])

  return { state, select }
}
